// client/src/api/templates.js
const API_BASE_URL = '/api'; // Your backend API base URL

/**
 * Helper to get headers without authentication token.
 * @returns {object} Headers object.
 */
const getHeaders = () => {
  return {
    'Content-Type': 'application/json'
  };
};

/**
 * Transforms backend template data (snake_case) to frontend format (camelCase)
 */
const transformTemplateFromBackend = (template) => {
  if (!template) {
    console.warn('transformTemplateFromBackend received null or undefined template');
    return {
      id: null,
      name: '',
      type: '',
      description: '',
      items: []
    };
  }

  return {
    ...template,
    items: (template.items || []).map(item => ({
      id: item.id,
      itemName: item.item_name,
      unitType: item.unit_type,
      unitPrice: parseFloat(item.unit_price || 0),
      description: item.description || '',
      isTaxable: Boolean(item.is_taxable),
      sortOrder: item.sort_order || 0
    }))
  };
};

/**
 * Transforms frontend template data (camelCase) to backend format (snake_case)
 */
const transformTemplateToBackend = (templateData) => {
  return {
    name: templateData.name,
    type: templateData.type || '',
    description: templateData.description || '',
    items: (templateData.items || []).map(item => ({
      id: item.id,
      itemName: item.itemName,
      unitType: item.unitType,
      unitPrice: parseFloat(item.unitPrice || 0),
      description: item.description || '',
      isTaxable: Boolean(item.isTaxable !== undefined ? item.isTaxable : true),
      sortOrder: item.sortOrder || 0
    }))
  };
};

/**
 * Fetches all templates for the authenticated user.
 * @returns {Promise<Array>} A promise that resolves to an array of template objects.
 */
export const fetchTemplates = async () => {
  const response = await fetch(`${API_BASE_URL}/templates`, {
    method: 'GET',
    headers: getHeaders(),
    credentials: 'include',
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || 'Failed to fetch templates');
  }

  const data = await response.json();
  console.log('Raw templates from backend:', data); // Debug log

  // Ensure data.templates is an array
  if (!Array.isArray(data.templates)) {
    console.warn('Invalid templates response, expected array', data);
    return { templates: [] };
  }

  return {
    templates: data.templates.map(template => ({
      ...template,
      // Transform any items if they exist (though list view usually doesn't include items)
      items: (template.items || []).map(item => ({
        id: item.id,
        itemName: item.item_name || item.itemName,
        unitType: item.unit_type || item.unitType,
        unitPrice: parseFloat(item.unit_price || item.unitPrice || 0),
        description: item.description || '',
        isTaxable: Boolean(item.is_taxable !== undefined ? item.is_taxable : item.isTaxable),
        sortOrder: item.sort_order || item.sortOrder || 0
      }))
    }))
  };
};

/**
 * Fetches a single template with its items.
 * @param {string} token - The authentication token.
 * @param {number} templateId - The ID of the template to fetch.
 * @returns {Promise<object>} A promise that resolves to the template object with items.
 */
export const fetchTemplateById = async (templateId) => {
  const headers = getHeaders();
  const response = await fetch(`${API_BASE_URL}/templates/${templateId}`, {
    method: 'GET',
    headers: headers,
    credentials: 'include',
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || 'Failed to fetch template details');
  }

  const data = await response.json();
  console.log('Raw template from backend:', data); // Debug log

  // Ensure data.template exists before transforming
  if (!data.template) {
    throw new Error('Template not found or invalid response from server');
  }

  return {
    template: transformTemplateFromBackend(data.template)
  };
};

/**
 * Creates a new template with its items.
 * @param {string} token - The authentication token.
 * @param {object} templateData - Object containing template name, type, description, and items array.
 * @returns {Promise<object>} A promise that resolves to the new template's info.
 */
export const createTemplate = async (templateData) => {
  const transformedData = transformTemplateToBackend(templateData);
    console.log('Sending template data to backend:', transformedData); // Debug log
  
    const headers = getHeaders();  const response = await fetch(`${API_BASE_URL}/templates`, {
    method: 'POST',
    headers: headers,
    body: JSON.stringify(transformedData),
    credentials: 'include',
  });

  if (!response.ok) {
    const errorData = await response.json();
    console.error('Create template error:', errorData); // Debug log
    throw new Error(errorData.message || errorData.errors?.[0]?.msg || 'Failed to create template');
  }
  return response.json();
};

/**
 * Updates an existing template with its items.
 * @param {string} token - The authentication token.
 * @param {number} templateId - The ID of the template to update.
 * @param {object} templateData - Object containing updated template name, type, description, and items array.
 * @returns {Promise<object>} A promise that resolves to a success message.
 */
export const updateTemplate = async (templateId, templateData) => {
  const transformedData = transformTemplateToBackend(templateData);
  console.log('Updating template with data:', transformedData); // Debug log
  
  const headers = getHeaders();  const response = await fetch(`${API_BASE_URL}/templates/${templateId}`, {
    method: 'PUT',
    headers: headers,
    body: JSON.stringify(transformedData),
    credentials: 'include',
  });

  if (!response.ok) {
    const errorData = await response.json();
    console.error('Update template error:', errorData); // Debug log
    throw new Error(errorData.message || errorData.errors?.[0]?.msg || 'Failed to update template');
  }
  return response.json();
};

/**
 * Deletes a template.
 * @param {number} templateId - The ID of the template to delete.
 * @returns {Promise<object>} A promise that resolves to a success message.
 */
export const deleteTemplate = async (templateId) => {
  const response = await fetch(`${API_BASE_URL}/templates/${templateId}`, {
    method: 'DELETE',
    headers: getHeaders(),
    credentials: 'include',
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || 'Failed to delete template');
  }
  return response.json();
};
